import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/utils/api';

export interface SearchResult {
  id: string;
  /** `'agent'` distinct from `'user'` — server emits it for
   *  rows where app_user.is_agent = true so the UI can render an
   *  Agent chip instead of "User" and surface agent-specific
   *  affordances (capability chips, agent icon). */
  type: 'profile' | 'post' | 'user' | 'agent' | 'tag';
  title: string;
  subtitle?: string;
  avatar?: string;
  link: string;
  /** True when this result is an agent — mirrors `type === 'agent'`
   *  but exposed as an explicit field so callers can filter without
   *  string-matching on type. */
  is_agent?: boolean;
  /** Agent capability keys (from AGENT_CAPABILITIES). Only populated
   *  when `type === 'agent'`. Empty array or absent for humans. */
  capabilities?: string[];
}

interface SearchResponse {
  results: SearchResult[];
  total: number;
}

// Universal search
export const search = createAsyncThunk(
  'search/query',
  async (query: string, { rejectWithValue }) => {
    try {
      const response = await api.get<SearchResponse>('/search', {
        params: { q: query, limit: 10 }
      });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Search failed';
      return rejectWithValue(message);
    }
  }
);

// Search users
export const searchUsers = createAsyncThunk(
  'search/users',
  async (query: string, { rejectWithValue }) => {
    try {
      const response = await api.get<SearchResponse>('/search/users', {
        params: { q: query, limit: 10 }
      });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'User search failed';
      return rejectWithValue(message);
    }
  }
);

// Search profiles (blockchain profiles)
export const searchProfiles = createAsyncThunk(
  'search/profiles',
  async (query: string, { rejectWithValue }) => {
    try {
      const response = await api.get<SearchResponse>('/search/profiles', {
        params: { q: query, limit: 10 }
      });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Profile search failed';
      return rejectWithValue(message);
    }
  }
);

// Search posts
export const searchPosts = createAsyncThunk(
  'search/posts',
  async (query: string, { rejectWithValue }) => {
    try {
      const response = await api.get<SearchResponse>('/search/posts', {
        params: { q: query, limit: 10 }
      });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Post search failed';
      return rejectWithValue(message);
    }
  }
);

// Search tags
export const searchTags = createAsyncThunk(
  'search/tags',
  async (query: string, { rejectWithValue }) => {
    try {
      const response = await api.get<SearchResponse>('/search/tags', {
        params: { q: query, limit: 10 }
      });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Tag search failed';
      return rejectWithValue(message);
    }
  }
);
